import { defineMessages } from 'react-intl';

export const DOMAIN_NAME = 'audio';

export const AudioMessages = defineMessages({
  havingTroubleStartingPlayback: {
    id: 't.havingTroubleStartingPlayback',
    defaultMessage:
      'We are having trouble starting playback, please refresh and if errors persist please contact support.',
  },
});

export const AudioPlayerKeys = Object.freeze({
  PLAYING: 'AUDIO_PLAYING',
  PAUSED: 'AUDIO_PAUSED',
  CURRENT_BUFFER_END: 'AUDIO_CURRENT_BUFFER_END',
  CURRENT_TIME: 'AUDIO_CURRENT_TIME',
  DURATION: 'AUDIO_DURATION',
  LOADING: 'AUDIO_LOADING',
  RESOURCE_URL: 'AUDIO_RESOURCE_URL',
  BUFFERING: 'AUDIO_BUFFERING',
  BUFFERED: 'AUDIO_BUFFERED',
  BUFFERED_REMAINING: 'AUDIO_BUFFERED_REMAINING',
  ERROR: 'AUDIO_ERROR',
  ERROR_MSG: 'AUDIO_ERROR_MSG',
  ERROR_OBJ: 'ERROR_OBJ',
  ERROR_TYPE: 'ERROR_TYPE',
  ERROR_COUNTS: 'AUDIO_ERROR_COUNTS',
  BANDWIDTH_ERRORS: 'AUDIO_BANDWIDTH_ERRORS',
  BANDWIDTH_DIFFERENCE: 'BANDWIDTH_DIFFERENCE',
  HAS_EXPERIENCED_BANDWIDTH_ERROR: 'HAS_EXPERIENCED_BANDWIDTH_ERROR',
  SRC: 'AUDIO_SRC',
  CURRENT_TIMESTAMP: 'CURRENT_TIMESTAMP',
  ACTIVE_TRACK_URL: 'ACTIVE_TRACK_URL',
  EXPLICITLY_PAUSED: 'EXPLICITLY_PAUSED',
  LAST_ERROR_TIMESTAMP: 'LAST_ERROR_TIMESTAMP',

  // metadata about the audio player service
  HARNESSES: 'AUDIO_HARNESSES',

  // included because of previous implementation, tbd
  PLAYLIST: 'AUDIO_PLAYLIST',
  TRACK_TOKEN: 'AUDIO_TRACK_TOKEN',

  HAS_WAKE_LOCK: 'HAS_WAKE_LOCK',
  AUX_PLAYBACK_PLAYING: 'AUX_PLAYBACK_PLAYING',
  AUX_PLAYBACK_URL: 'AUX_PLAYBACK_URL',
  /* number of time we have retried initialization of audio lib */
  INIT_AUDIO_RETRIES: 'INIT_AUDIO_RETRIES',
  INIT: 'INIT',
});

export const AudioFormats = Object.freeze({
  LOWEST_QUALITY: 'aacplus',
  HIGHEST_QUALITY: 'mp3-hifi',
  HIGHEST_QUALITY_ENCRYPTABLE: 'aacplus-hifi',
} as const);

export const AudioEncryptionFormats = Object.freeze({
  BITMASK: 'bitmask',
  AES: 'aes',
});

export const AudioTypes = Object.freeze({
  SAMPLE: 'sample',
  TRACK: 'track',
  PODCAST: 'podcast',
  AD: 'ad',
  VOICETRACK: 'voicetrack',
});

export const AudioFormatToCommonTypes = Object.freeze({
  [AudioFormats.LOWEST_QUALITY]: 'm4a',
  [AudioFormats.HIGHEST_QUALITY_ENCRYPTABLE]: 'm4a',
  [AudioFormats.HIGHEST_QUALITY]: 'mp3',
});

export const AudioEncodingFormats = Object.freeze({
  M3U8: 'm3u8',
  MP3: 'mp3',
  AACPLUS: 'aacplus',
  AACPLUS_HIFI: 'aacplus-hifi',
  M4A: 'm4a',
  MP4: 'mp4',
  F4M: 'f4m',
});

export const AudioFormatCodecs = Object.freeze({
  MP4: 'mp4a.40.2',
});

export const CommonAudioFormatToMediaElementTypes = Object.freeze({
  [AudioEncodingFormats.M4A]: 'audio/m4a',
  [AudioEncodingFormats.AACPLUS]: 'audio/m4a',
  [AudioEncodingFormats.AACPLUS_HIFI]: 'audio/m4a',
  [AudioEncodingFormats.MP3]: 'audio/mp3',
});

/**
 * Types of audio harnesses supported
 *
 * @typedef {object} AudioHarnessName
 * @type {object}
 * @property
 */
export const AudioHarnessName = {
  STROBE: 'strobe',
  MEDIA_ELEMENT: 'mediaelement',
  SXM_AUDIO_PLAYER: 'SXMAudioPlayer',
};

export const AudioHarnessType = {
  P_MEDIA_ELEMENT: 'P_MEDIA_ELEMENT',
  SXM_MEDIA_ELEMENT: 'SXM_MEDIA_ELEMENT',
  SXM_WEB_AUDIO_API: 'SXM_WEB_AUDIO_API',
};

// Config for what harnesses to use for what filetypes in what browsers
export const HarnessConfig = Object.freeze({
  Safari: [
    {
      name: AudioHarnessName.MEDIA_ELEMENT,
      formats: [
        AudioEncodingFormats.M3U8,
        AudioEncodingFormats.MP3,
        AudioEncodingFormats.AACPLUS,
        AudioEncodingFormats.AACPLUS_HIFI,
        AudioEncodingFormats.M4A,
        AudioEncodingFormats.MP4,
      ],
    },
  ],
  Default: [
    {
      name: AudioHarnessName.MEDIA_ELEMENT,
      formats: [
        AudioEncodingFormats.MP3,
        AudioEncodingFormats.AACPLUS,
        AudioEncodingFormats.AACPLUS_HIFI,
        AudioEncodingFormats.M4A,
        AudioEncodingFormats.MP4,
      ],
    },
    {
      name: AudioHarnessName.STROBE,
      formats: [AudioEncodingFormats.F4M],
    },
  ],
});

export const sxmMediaelementAudioTypes = [AudioTypes.AD, AudioTypes.PODCAST]; // AudioTypes.SAMPLE, AudioTypes.VOICETRACK

export const SXMHarnessConfig = {
  name: AudioHarnessName.SXM_AUDIO_PLAYER,
  formats: [
    AudioEncodingFormats.MP3,
    AudioEncodingFormats.AACPLUS,
    AudioEncodingFormats.AACPLUS_HIFI,
    AudioEncodingFormats.M4A,
    AudioEncodingFormats.MP4,
  ],
  audioTypes: [AudioTypes.TRACK, ...sxmMediaelementAudioTypes],
};

// Config for how many providers we want to keep around in different browsers
// this is important for Safari because it won't allow the creation of new Audio
// elements in the background
export const maxProviderConfig = Object.freeze({
  Safari: 1,
  Default: 5,
});

export const AUX_GAIN_DEFAULT = 0;

export const VolumeConstants = Object.freeze({
  DEFAULT_VOLUME: 50,
  DEFAULT_UNMUTE_VOLUME: 20,
});

// Based on https://developer.mozilla.org/en-US/docs/Web/API/MediaError with
// jPlayer errors that didn't map well thrown in
export const CommonAudioErrorCodes = Object.freeze({
  UNKNOWN: 'AUDIO_PLAYBACK_ERR_UNKNOWN',
  ABORTED: 'AUDIO_PLAYBACK_ERR_ABORTED',
  NETWORK: 'AUDIO_PLAYBACK_ERR_NETWORK',
  DECODE: 'AUDIO_PLAYBACK_ERR_DECODE',
  SRC_NOT_SUPPORTED: 'AUDIO_PLAYBACK_ERR_SRC_NOT_SUPPORTED',
  BROWSER_AUTOPLAY_BLOCKED: 'AUDIO_PLAYBACK_ERR_BROWSER_AUTOPLAY_BLOCKED',
  INIT_TIMEOUT: 'AUDIO_PLAYBACK_INIT_TIMEOUT',
  EXPIRED: 'AUDIO_PLAYBACK_ERR_EXPIRED',
  SERVICE_ERROR: 'AUDIO_PLAYBACK_ERR_SERVICE_ERROR',
});

export const RecoverableErrors = [
  CommonAudioErrorCodes.NETWORK,
  CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
  CommonAudioErrorCodes.DECODE,
];

export const AudioContentHost = Object.freeze({
  INTERNAL: 'internal',
  EXTERNAL: 'external',
});

export const BROWSER_AUTOPLAY_BLOCKED_PROMISE_ERROR = 'NotAllowedError';
export const INIT_TIMEOUT_MS = 7000;
export const MAX_BANDWIDTH_WARNINGS = 10;
export const ERROR_RATE_LIMIT_MS = 10000;
export const MAX_AUDIO_RETRIES = 5;

export const MAX_BUFFERED_AUDIO_SECONDS = 60;
export const MIN_BUFFERED_AUDIO_SECONDS = 30;
export const MAX_BUFFER_CHUNK = 1000000; // 1MB
export const BUFFER_RETRY_SECONDS = 20;
